<div class="all-tests-dashboard">
  <!-- Page Header -->
  <div class="page-header">
    <pdq-heading element="h3">A/B test Performance Dashboard</pdq-heading>
    <p class="page-subtitle">Comprehensive view of all testing activity</p>
  </div>

  <!-- Summary KPIs Section -->
  @if (loadingSummary || hasAnnualizedRevenue) {
    <div class="summary-section">
      <impact-metrics-header
        [metrics]="overviewMetrics"
        [loading]="loadingSummary"
        [error]="errorSummary"
        [showRoi]="!hideRoi"
        [showImplementedComponents]="false"
        [showActiveSegments]="false"
        [showRunningTests]="true"
        (refresh)="onRefreshSummary()">
      </impact-metrics-header>
    </div>
  }

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-group">
      <mat-select
        class="table-columns-select"
        panelClass="pdq-mat-select--panel__multi"
        [(ngModel)]="selectedColumns"
        (selectionChange)="toggleColumn()"
        [disableOptionCentering]="true"
        multiple
        matTooltipClass="pdq-mat-tooltip"
        [matTooltip]="'Edit columns'">
        <div class="pdq-mat-select--panel--header">
          Showing {{ selectedColumns.length + '/' + COLUMN_OPTIONS.length }} columns
        </div>
        @for (column of COLUMN_OPTIONS; track column) {
          <mat-option selected="selected" [value]="column.prop">
            {{ column.name }}
          </mat-option>
        }
      </mat-select>

      <pdq-input
        class="search-input"
        placeholder="Search by test name..."
        [withIcon]="true"
        icon="search"
        [debounceTime]="300"
        background="light"
        (onKeyup)="onSearchInput($event)">
      </pdq-input>

      <pdq-dropdown
        [options]="testTypeDropdownOptions"
        [defaultOption]="selectedTestTypeOption"
        (onChange)="onTestTypeChange($event)">
      </pdq-dropdown>

      <pdq-dropdown
        [options]="statusDropdownOptions"
        [defaultOption]="selectedStatusOption"
        (onChange)="onStatusChange($event)">
      </pdq-dropdown>
    </div>

    <div class="section-actions">
      <pdq-date-range-picker
        (onApply)="onDateRangeChange($event)"
        [createdAt]="shopCreated()"
        [defaultPreset]="defaultRange"
        petitionType="range"
        positionRight="true"
        background="light">
      </pdq-date-range-picker>
    </div>
  </div>

  <!-- Tests Table Section -->
  <div class="table-container">
    <div class="table-wrapper">
      <table class="tests-table">
        <thead>
          <tr>
            @for (col of COLUMN_OPTIONS; track col.prop) {
              @if (isColumnVisible(col.prop)) {
                <th
                  [class]="col.cssClass"
                  [class.sortable]="!!col.sortKey"
                  (click)="col.sortKey && onSort(col.sortKey)"
                  [matTooltip]="col.tooltip"
                  matTooltipClass="pdq-mat-tooltip"
                  matTooltipPosition="above">
                  <span class="th-content">
                    {{ col.name }}
                    @if (col.sortKey) {
                      <span class="sort-icon">{{ getSortIcon(col.sortKey) }}</span>
                    }
                  </span>
                </th>
              }
            }
          </tr>
        </thead>
        <tbody>
          @if (loadingTests) {
            @for (row of [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; track row) {
              <tr class="skeleton-row">
                @for (col of COLUMN_OPTIONS; track col.prop) {
                  @if (isColumnVisible(col.prop)) {
                    <td><div class="skeleton-cell"></div></td>
                  }
                }
              </tr>
            }
          } @else {
            @for (test of tests; track test.testId; let i = $index) {
              <tr class="test-row" (click)="onTestClick(test)">
                <!-- Test Name -->
                @if (isColumnVisible('testName')) {
                  <td class="col-test-name">
                    <div class="test-name-cell-row">
                      @if (maxxAvailable && test.maxxAnalysis; as maxxState) {
                        <maxx-analysis-trigger
                          [state]="maxxState"
                          [triggerId]="'maxx-trigger-' + test.testId"
                          [panelId]="'maxx-panel-' + test.testId"
                          (triggerClick)="onMaxxTriggerClick(test, $event)">
                        </maxx-analysis-trigger>
                      }
                      <div class="test-name-cell">
                        <span class="test-name">{{ test.testName }}</span>
                        <span class="test-dates">{{ test.formattedDateRange }}</span>
                      </div>
                    </div>
                  </td>
                }

                <!-- Type -->
                @if (isColumnVisible('type')) {
                  <td class="col-type">
                    <span class="type-badge">{{ test.type }}</span>
                  </td>
                }

                <!-- Status -->
                @if (isColumnVisible('status')) {
                  <td class="col-status">
                    <span class="status-badge" [class.running]="test.isRunning">
                      {{ test.isRunning ? 'Running' : 'Concluded' }}
                    </span>
                  </td>
                }

                <!-- AI Hypothesis -->
                @if (isColumnVisible('hypothesis')) {
                  <td
                    class="col-hypothesis"
                    [matTooltip]="test.aiHypothesis"
                    matTooltipClass="pdq-mat-tooltip"
                    matTooltipPosition="above">
                    <div class="hypothesis-cell" [class.empty]="!test.aiHypothesis">
                      <p class="hypothesis-text">{{ test.aiHypothesis || '-' }}</p>
                    </div>
                  </td>
                }

                <!-- Strategy -->
                @if (isColumnVisible('strategy')) {
                  <td class="col-strategy">
                    @if (test.strategyName && test.strategyCatalogId) {
                      <span class="strategy-link" (click)="onStrategyClick(test, $event)">{{ test.strategyName }}</span>
                    } @else if (test.strategyName) {
                      <span class="strategy-text">{{ test.strategyName }}</span>
                    } @else {
                      <span class="na-text">-</span>
                    }
                  </td>
                }

                <!-- Winner -->
                @if (isColumnVisible('winner')) {
                  <td
                    class="col-winner"
                    [matTooltip]="test.segmentWinner"
                    matTooltipClass="pdq-mat-tooltip"
                    matTooltipPosition="above">
                    @if (test.isRunning) {
                      <span class="na-text">-</span>
                    } @else if (test.segmentWinner) {
                      <div class="winner-cell">
                        <span class="winner-text">{{ test.segmentWinner }}</span>
                        @if (test.hasWinner && test.outliersImplementation) {
                          <span class="winner-note">{{ test.outliersDisplay }}</span>
                        }
                      </div>
                    } @else {
                      <span class="na-text">No winner</span>
                    }
                  </td>
                }

                <!-- Impact -->
                @if (isColumnVisible('impact')) {
                  <td class="col-impact">
                    @if (test.isRunning) {
                      <div class="impact-cell">
                        <span class="tbd-text">TBD</span>
                        <span class="impact-metric">{{ test.impactMetric }}</span>
                      </div>
                    } @else if (test.impactAmount < 0) {
                      <span class="na-text">-</span>
                    } @else {
                      <div class="impact-cell">
                        <span class="impact-value" [class.positive]="test.impactAmount > 0">
                          {{ test.impactAmount > 0 ? '+' : ''
                          }}{{ test.impactAmount | currency: currencySymbol : 'symbol' : '1.2-2' }}
                        </span>
                        <span class="impact-metric">{{ test.impactMetric }}</span>
                      </div>
                    }
                  </td>
                }

                <!-- Stat. Sig. -->
                @if (isColumnVisible('statSig')) {
                  <td class="col-stat-sig">
                    @if (!test.hasWinner) {
                      <span class="na-text">-</span>
                    } @else {
                      <span class="stat-sig-value">{{ test.statSig | number: '1.1-1' }}%</span>
                    }
                  </td>
                }
              </tr>

              @if (test.maxxAnalysis?.expanded ? test.maxxAnalysis : null; as maxxState) {
                <tr class="maxx-panel-row">
                  <td class="col-maxx-panel" [attr.colspan]="visibleColumnCount">
                    <maxx-analysis-panel
                      [state]="maxxState"
                      [panelId]="'maxx-panel-' + test.testId"
                      [testName]="test.testName"
                      (collapse)="onMaxxCollapse(test)"
                      (retry)="onMaxxRetry(test)"
                      (continueInChat)="onMaxxContinueInChat($event)">
                    </maxx-analysis-panel>
                  </td>
                </tr>
              }
            }
          }
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    @if (!loadingTests && pagination.totalPages >= 1) {
      <div class="pagination-section">
        <div class="pagination-info">
          Showing {{ pagination.page * pagination.pageSize + 1 }} -
          {{ pagination.hasNextPage ? (pagination.page + 1) * pagination.pageSize : pagination.totalItems }} of
          {{ pagination.totalItems }} tests
        </div>
        <pdq-pagination
          [totalCountItems]="pagination.totalItems"
          [allPages]="allPages"
          [activePage]="activePage"
          [totalPages]="pagination.totalPages"
          [loading]="loadingTests"
          (onPageChange)="onPageChange($event)">
        </pdq-pagination>
      </div>
    }
  </div>
</div>
